'use client';

/**
 * DataRoomSourceGroup Component
 *
 * Collapsible section that groups documents by their source.
 * Shows source name, label, document count, and a list of DataRoomDocumentRow items.
 */

import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { GlassChip } from '../GlassChip/GlassChip';
import { GlassIconButton } from '../GlassIconButton/GlassIconButton';
import { DataRoomDocumentRow, type DataRoomDocumentRowProps } from './DataRoomDocumentRow';

// ============================================================================
// Types
// ============================================================================

export interface DataRoomSourceGroupProps {
  /** Name of the source (e.g., borrower name) */
  sourceName: string;
  /** Label for the source type (e.g., "Primary Borrower") */
  sourceLabel: string;
  /** Documents belonging to this source */
  documents: Omit<
    DataRoomDocumentRowProps,
    'onView' | 'onUnlink' | 'showUnlink' | 'onVerify' | 'onReject' | 'onRequestReupload' | 'showVerificationControls'
  >[];
  /** Whether the section is expanded */
  isExpanded: boolean;
  /** Callback to toggle expansion */
  onToggle: () => void;
  /** Callback when viewing a document */
  onView?: (documentId: string) => void;
  /** Callback when unlinking a document */
  onUnlink?: (documentId: string) => void;
  /** Callback when verify button is clicked */
  onVerify?: (documentId: string) => void;
  /** Callback when reject button is clicked */
  onReject?: (documentId: string) => void;
  /** Callback when request reupload button is clicked */
  onRequestReupload?: (documentId: string) => void;
  /** Whether to show verification action controls */
  showVerificationControls?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export const DataRoomSourceGroup = ({
  sourceName,
  sourceLabel,
  documents,
  isExpanded,
  onToggle,
  onView,
  onUnlink,
  onVerify,
  onReject,
  onRequestReupload,
  showVerificationControls,
}: DataRoomSourceGroupProps): React.ReactElement => {
  return (
    <Box>
      {/* Header */}
      <Stack
        direction="row"
        spacing={1}
        onClick={onToggle}
        sx={{
          alignItems: 'center',
          py: 1,
          px: 1.5,
          cursor: 'pointer',
          borderRadius: 1,

          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
      >
        <GlassIconButton
          size="small"
          variant="standard"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
        >
          {isExpanded ? <ExpandLess /> : <ExpandMore />}
        </GlassIconButton>

        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          {sourceName}
        </Typography>

        <GlassChip label={sourceLabel} size="small" variant="outlined" color="primary" glowOnHover={false} />

        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
          }}
        >
          {documents.length}
        </Typography>
      </Stack>
      {/* Document list */}
      <Collapse in={isExpanded}>
        <Box sx={{ pl: 2 }}>
          {documents.map((doc) => (
            <DataRoomDocumentRow
              key={doc.documentId}
              {...doc}
              onView={onView}
              onUnlink={onUnlink}
              onVerify={onVerify}
              onReject={onReject}
              onRequestReupload={onRequestReupload}
              showVerificationControls={showVerificationControls}
            />
          ))}
        </Box>
        <Divider sx={{ mt: 1 }} />
      </Collapse>
    </Box>
  );
};

DataRoomSourceGroup.displayName = 'DataRoomSourceGroup';

export default DataRoomSourceGroup;
